import {
  AppBar,
  Box,
  Grid,
  IconButton,
  Stack,
  Toolbar,
} from "@mui/material";
import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { sendEvent } from "../../Utils/analytics";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CSkeleton from "../CSkeleton/CSkeleton";
import { useMediaQuery } from "../CustomHook/useMediaQuery";
import FormatNumberVisible from "../FormatData/FormatNumberVisible";
import ProfileMenu from "../ProfileMenu/ProfileMenu";

const Navbar = () => {
  const { isLoggedIn, user, logout, showBanner, setShowBanner } = useAuthState();
  const isDesktop = useMediaQuery();
  const [loading, setLoading] = useState(true);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);

  const shouldShowBanner = showBanner && bannerVisible && !hasScrolled;

  useEffect(() => {
    const isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    if (isMobileDevice) {
      setShowBanner(true);
    }
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [setShowBanner]);

  const handleCloseBanner = () => {
    setBannerVisible(false);
  };

  const handleOpenBanner = () => {
    var bannerUrl = getRemoteValue("banner_url");
    if (bannerUrl && bannerUrl !== "banner_url") {
      window.open(bannerUrl);
    }
  };

  // Get banner content from remote config
  const bannerTitle = getRemoteValue("banner_title");
  const bannerSubtitle = getRemoteValue("banner_subtitle");
  const bannerIcon = getRemoteValue("banner_icon");

  return (
    <Grid item xs="auto">
      <AppBar
        elevation={0}
        sx={{
          // CRL-3241 `.appbar`: frosted glass with no shadow and no bottom
          // border — the edge is a progressive blur fading into the content.
          bgcolor: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(18px) saturate(180%)",
          WebkitBackdropFilter: "blur(18px) saturate(180%)",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            height: 46,
            pointerEvents: "none",
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0))",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            maskImage:
              "linear-gradient(to bottom, #000 0%, #000 15%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, #000 0%, #000 15%, transparent 100%)",
          },
        }}
        className="mx-auto"
      >
        {/* Banner Section */}
        {shouldShowBanner && (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div className="flex items-center gap-3 flex-1">
              {/* Icon */}
              <Box
                component="img"
                src={bannerIcon}
                alt="banner icon"
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  padding: "4px",
                }}
              />

              {/* Text Content */}
              <div className="flex flex-col">
                <span className="font-bold text-sm text-black">
                  {bannerTitle}
                </span>
                <span className="text-xs text-gray-600">
                  {bannerSubtitle}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Open Button */}
              <button
                onClick={handleOpenBanner}
                style={{
                  backgroundColor: "#191B1E",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "14px",
                  padding: "8px 24px",
                  borderRadius: "9999px",
                  border: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginLeft: "16px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#1E293B")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#191B1E")
                }
              >
                {getRemoteValue("banner_button_text")}
              </button>

              {/* Close Button */}
              <IconButton
                onClick={handleCloseBanner}
                size="small"
                sx={{
                  color: "#666",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </IconButton>
            </div>
          </Box>
        )}

        {/* Navbar Section */}
        <Toolbar
          className="py-4 px-[16px] md:!px-[4%] lg:!px-[8%]"
          sx={{
            minHeight: "auto !important",
            paddingLeft: 0,
            paddingRight: 0,
            "@media (max-width: 768px)": {
              paddingLeft: "16px",
              paddingRight: "16px",
            },
          }}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-20">
              <Link to="/">
                {loading && (
                  <Stack alignItems={"end"}>
                    <CSkeleton
                      variant="rectangular"
                      animation="wave"
                      sx={{
                        width: 84,
                        height: 30,
                        "@media (min-width: 768.5px)": {
                          width: 96,
                          height: 36,
                        },
                      }}
                    />
                    <CSkeleton
                      variant="rectangular"
                      animation="wave"
                      sx={{
                        width: 70,
                        height: 13,
                        "@media (min-width: 768.5px)": {
                          width: 100,
                          height: 20,
                        },
                      }}
                    />
                  </Stack>
                )}
                <Box
                  component="img"
                  onLoad={() => setLoading(false)}
                  sx={{
                    display: loading ? "none" : "block",
                    width: 84,
                    height: "auto",
                    "@media (min-width: 768.5px)": {
                      width: 96,
                      height: "auto",
                    },
                  }}
                  alt="kodi+"
                  src="/kodi-wordmark.svg"
                />
              </Link>
            </div>
            {isLoggedIn() && (
              <div className="hidden md:flex items-center gap-3 text-black">
                <div className="flex-row text-right">
                  <div className="flex justify-end">
                    <div className="font-bold text-sm	">{user?.firstName}</div>
                    <div className="font-bold text-sm pl-1	">
                      {user?.lastName}
                    </div>
                  </div>
                  <div className="text-sm text-[#64748B]">
                    {FormatNumberVisible(user?.phoneNumber)}
                  </div>
                </div>
                <IconButton
                  aria-label="logout"
                  sx={{
                    width: "44px",
                    height: "44px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                  onClick={() => {
                    logout();
                    sendEvent(`click_logout_button`, {});
                    mixpanel.reset();
                  }}
                >
                  <img
                    src={"/icons/logout-icon.svg"}
                    width={"16px"}
                    height={"16px"}
                    alt="logout"
                  />
                </IconButton>
              </div>
            )}
            {!isDesktop && <ProfileMenu />}
          </div>
        </Toolbar>
      </AppBar>
      {/* Dynamic spacer - includes both Toolbar and Banner height */}
      <Box
        sx={{
          // Toolbar height (default navbar height)
          minHeight: "64px",
          // Add banner height when visible
          ...(shouldShowBanner && {
            minHeight: "132px", // 64px (toolbar) + 68px (banner)
            "@media (max-width: 768px)": {
              minHeight: "132px",
            },
          }),
        }}
      />
    </Grid>
  );
};

export default Navbar;
